import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Banknote, Scale } from "lucide-react";

const typeColor = {
  "Payment (Reimbursement)": "text-red-700",
  "Payment (Advance)": "text-orange-700",
  "Payment (Advance excess)": "text-red-700",
  "Refund (Advance)": "text-emerald-700",
  "Grant Receipt": "text-emerald-700",
  "Recovery": "text-amber-700",
};

export default function CashStatement() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/projects").then(r => setProjects(r.data)); }, []);
  const load = () => {
    const params = new URLSearchParams();
    if (selectedProject && selectedProject !== "__ALL__") params.set("project_id", selectedProject);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/reports/cash-statement?${params}`).then(r => setData(r.data));
  };
  useEffect(load, [selectedProject]);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Reports · Cash Statement</div>
        <h1 className="font-display text-3xl font-extrabold mt-1">Cash Statement (Payment-Date)</h1>
        <p className="text-sm text-slate-500 mt-1">Bank-book view. Ties to expenditure report and highlights the difference (advances that are cash-out but not yet expenditure).</p>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label>Project (or all)</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger data-testid="cash-project-select"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__ALL__">All projects</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>From</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="cash-from-input" /></div>
          <div><Label>To</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="cash-to-input" /></div>
          <Button onClick={load} className="bg-[#0F172A]" data-testid="cash-apply-btn">Apply</Button>
        </CardContent>
      </Card>

      {!data ? <div className="text-slate-500 text-sm">Loading…</div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            <Card><CardContent className="p-4"><div className="text-xs uppercase text-slate-500">Cash Out</div><div className="font-display font-bold text-2xl mt-1 text-red-700">₹ {data.totals.debit.toLocaleString()}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs uppercase text-slate-500">Cash In</div><div className="font-display font-bold text-2xl mt-1 text-emerald-700">₹ {data.totals.credit.toLocaleString()}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs uppercase text-slate-500">Net</div><div className={`font-display font-bold text-2xl mt-1 ${data.totals.net < 0 ? "text-red-700" : "text-emerald-700"}`}>₹ {data.totals.net.toLocaleString()}</div></CardContent></Card>
            <Card className="border-slate-300"><CardContent className="p-4">
              <div className="text-xs uppercase text-slate-500 flex items-center gap-1"><Scale className="w-3 h-3" /> Reconciliation Δ</div>
              <div className={`font-display font-bold text-2xl mt-1 ${Math.abs(data.reconciliation.difference) < 0.01 ? "text-emerald-700" : "text-amber-700"}`}>₹ {data.reconciliation.difference.toLocaleString()}</div>
              <div className="text-[10px] text-slate-500 mt-1">Cash − Accrual − Advances</div>
            </CardContent></Card>
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                  <tr className="border-b">
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Reference</th>
                    <th className="text-left px-3 py-2">Counterparty</th>
                    <th className="text-right px-3 py-2">Debit (Cash Out)</th>
                    <th className="text-right px-3 py-2">Credit (Cash In)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.length === 0 && <tr><td colSpan={6} className="text-center px-3 py-10 text-slate-500">No entries.</td></tr>}
                  {data.entries.map((e, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`cash-entry-${i}`}>
                      <td className="px-3 py-2 font-mono text-xs">{e.date || "—"}</td>
                      <td className={`px-3 py-2 ${typeColor[e.type] || ""}`}>{e.type}</td>
                      <td className="px-3 py-2 font-mono text-xs">{e.reference || "—"}</td>
                      <td className="px-3 py-2">{e.counterparty || "—"}</td>
                      <td className={`px-3 py-2 text-right font-mono ${e.debit ? "text-red-700 font-semibold" : "text-slate-300"}`}>{e.debit ? `₹ ${e.debit.toLocaleString()}` : "—"}</td>
                      <td className={`px-3 py-2 text-right font-mono ${e.credit ? "text-emerald-700 font-semibold" : "text-slate-300"}`}>{e.credit ? `₹ ${e.credit.toLocaleString()}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t">
                  <tr>
                    <td colSpan={4} className="px-3 py-2 text-right font-semibold">Totals</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-red-700">₹ {data.totals.debit.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">₹ {data.totals.credit.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>

          <Card className="mt-6 bg-slate-50 border-dashed">
            <CardContent className="p-4 text-xs">
              <div className="font-semibold mb-1 flex items-center gap-1"><Banknote className="w-3 h-3" /> Reconciliation</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-2 font-mono">
                <div><span className="text-slate-500">Cash payments out:</span> ₹ {data.reconciliation.cash_payments_out.toLocaleString()}</div>
                <div><span className="text-slate-500">Accrual (paid RMBs):</span> ₹ {data.reconciliation.accrual_paid_reimbursements.toLocaleString()}</div>
                <div><span className="text-slate-500">Δ (advance impact):</span> ₹ {data.reconciliation.difference.toLocaleString()}</div>
                <div data-testid="cash-advance-refunds"><span className="text-slate-500">Advance refunds in:</span> ₹ {(data.reconciliation.advance_refunds_in || 0).toLocaleString()}</div>
              </div>
              <div className="text-slate-500 mt-2">{data.reconciliation.note}</div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
